import { useProducts } from "./products/products.mjs";
import { lastItem } from "../utils/collections.mjs";
import { get, set } from "idb-keyval";
import { intersection } from "lodash-es";
import { computed, onMounted, reactive, watch } from "vue";
import { useRoute } from "vue-router";
import { useState } from "#app";
const DB_KEY = "compare_skus";
function useStoredSkus() {
  const skus = useState("compare-skus", () => []);
  const shouldShowComparison = reactive({
    value: true,
    smallScreenOnly: false
  });
  return {
    skus,
    shouldShowComparison
  };
}
export function useComparison(_useStoredSkus = useStoredSkus, _useProducts = useProducts) {
  const route = useRoute();
  const { skus, shouldShowComparison } = _useStoredSkus();
  const variables = computed(() => {
    const currentSkus = skus.value;
    return currentSkus.length ? {
      pageSize: 4,
      withAttributes: true,
      filter: { sku: { in: skus.value.map((item) => item.sku) } }
    } : {
      pageSize: 0
    };
  });
  const { products, isFetching } = _useProducts(variables, {
    fetchOnMount: false
  });
  watch(route, () => {
    shouldShowComparison.value = true;
    shouldShowComparison.smallScreenOnly = false;
  });
  function setShouldShowComparison(newValue, smallScreenOnly = false) {
    shouldShowComparison.value = newValue;
    shouldShowComparison.smallScreenOnly = smallScreenOnly;
  }
  onMounted(async () => {
    const items = await get(DB_KEY) || [];
    if (!items.length && !skus.value.length) {
      return;
    }
    skus.value = [...new Set(items)];
  });
  const itemsWithAddNodes = computed(() => {
    const items = [...products.value];
    const renderItems = skus.value.map((sku, i) => {
      const item = items.find((item2) => item2?.sku === sku.sku);
      if (item) {
        return item;
      } else {
        return {
          id: i * -1,
          name: sku.sku,
          sku: sku.sku,
          thumb: {
            src: "/images/default-product-img.png",
            alt: "Dummy product image"
          },
          stock: 100
        };
      }
    });
    if (renderItems.length < 4) {
    }
    return renderItems;
  });
  const category = computed(() => {
    const firstItem = products.value[0];
    if (!firstItem || !firstItem.categories) {
      return { url: "", name: "" };
    }
    const lastCategory = lastItem(firstItem.categories);
    return { url: lastCategory?.url_path || "", name: lastCategory?.name };
  });
  const attributesRef = {
    dimensions: [
      "indoor_unit_dimensions_wxhxd_m",
      "attr_outdoor_unit_dimensions_wxhxd_",
      "net_weight"
    ]
  };
  const attributes = computed(() => {
    const attributes2 = products.value.reduce(
      (attrs, item, itemIdx) => {
        item.attributes?.forEach((attr, idx) => {
          const category2 = Object.keys(attributesRef).find(
            (key) => attributesRef[key].includes(
              attr.key
            )
          ) ?? "features";
          if (!attrs[category2][attr.key]) {
            attrs[category2][attr.key] = {
              key: attr.key,
              title: attr.label,
              values: [],
              idx
            };
          }
          attrs[category2][attr.key].values[itemIdx] = attr.value;
        });
        return attrs;
      },
      { features: {}, dimensions: {} }
    );
    return attributes2;
  });
  return {
    products,
    skus,
    isFetching,
    itemsWithAddNodes,
    category,
    shouldShowComparison,
    setShouldShowComparison,
    attributes
  };
}
export function useIsInComparison(sku, _useStoredSkus = useStoredSkus) {
  const { skus } = _useStoredSkus();
  return computed(() => {
    return skus.value.some((item) => item.sku === sku);
  });
}
export async function compareItem(sku) {
  const { skus } = useStoredSkus();
  if (skus.value.length === 0) {
    await updateState([...skus.value, sku]);
    return;
  }
  if (intersection(lastItem(skus.value).categoryIds, sku.categoryIds).length > 0) {
    await updateState([...skus.value, sku]);
    return;
  }
  throw new Error("invalidComparison");
}
export async function removeItem(sku, _useStoredSkus = useStoredSkus, _updateState = updateState) {
  const { skus } = _useStoredSkus();
  skus.value = skus.value.filter((item) => {
    return item.sku !== sku;
  });
  await _updateState(skus.value);
}
export async function compareClearItems(_useStoredSkus = useStoredSkus) {
  return await updateState([], _useStoredSkus);
}
async function updateState(items, _useStoredSkus = useStoredSkus) {
  const { skus } = _useStoredSkus();
  const uniqueSku = [...new Set(items)];
  await set(DB_KEY, uniqueSku);
  skus.value = uniqueSku;
  return skus.value;
}
